import { useModalState } from "@src/lib/ui-react";
import { Stack } from "@mui/material";
import { RecentlyVisitedPage, useRecentlyVisited } from "@src/appV2/lib/utils/useRecentlyVisited";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";

import { NavigationMenuSectionTitleDesktop } from "../../../../../appV2/redesign/AppLayout/Desktop/Navigation/MenuSectionTitle";
import { useAccountSwitcher } from "../../Shared/AccountSwitcher/useAccountSwitcher";
import { Button } from "@src/appV2/redesign/components/Button";
import { RecentlyViewedDialog } from "@src/appV2/redesign/AppLayout/Desktop/RecentlyViewedDialog";

// Default number of items to show in the UI
export const CONFIG_MAX_VISIBLE_ITEMS = 3;

interface RecentlyViewedSectionDesktopProps {
  admin: boolean;
}

export function RecentlyViewedSectionDesktop(props: RecentlyViewedSectionDesktopProps) {
  const { admin } = props;

  const [recentVisited] = useRecentlyVisited();
  const { onUserClicked } = useAccountSwitcher();
  const recentlyViewedDialogState = useModalState();
  const { tu } = useTu(I18N_NAMESPACES.navigation);

  // Only show for admin users
  if (!admin) {
    return null;
  }

  // Don't show if there are no recently visited items
  if (!recentVisited || recentVisited.length === 0) {
    return null;
  }

  const visibleItems = recentVisited.slice(0, CONFIG_MAX_VISIBLE_ITEMS);

  const hasMoreItems = recentVisited.length > CONFIG_MAX_VISIBLE_ITEMS;

  return (
    <>
      <NavigationMenuSectionTitleDesktop>{tu("Recently Viewed")}</NavigationMenuSectionTitleDesktop>
      <Stack gap={2}>
        {visibleItems.map((item) => (
          <Button
            key={item.id}
            variant="outlined"
            onClick={() => {
              onUserClicked({
                userId: item.id,
                name: item.title,
                title: item.title,
                type: item.type as "AGENT" | "WORKPLACE",
              });
            }}
            endIconType="hyperlink"
            sx={(theme) => ({
              height: "auto",
              backgroundColor: theme.palette.background?.baseDark,
              color: theme.palette.base?.strong,
              "& .MuiButton-endIcon": {
                color: theme.palette.icon?.muted,
              },
            })}
          >
            {item.title}
          </Button>
        ))}

        {hasMoreItems && (
          <Button
            variant="link"
            size="small"
            onClick={recentlyViewedDialogState.openModal}
            sx={(theme) => ({ color: theme.palette.base?.muted })}
          >
            {tu("View More")}
          </Button>
        )}

        {
          <RecentlyViewedDialog
            modalState={recentlyViewedDialogState}
            recentVisited={recentVisited}
            onUserClicked={(item: RecentlyVisitedPage) => {
              onUserClicked({
                userId: item.id,
                name: item.title,
                title: item.title,
                type: item.type as "AGENT" | "WORKPLACE",
              });
            }}
          />
        }
      </Stack>
    </>
  );
}
